import React, { useMemo, useState, useRef } from 'react';
import { API_BASE_URL } from '../config';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button, 
  CircularProgress, 
  Alert,
  LinearProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox
} from '@mui/material';
import { 
  CloudUpload as UploadIcon, 
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as BlankCircleIcon
} from '@mui/icons-material';
import axios from 'axios';
import { pollCsvUploadJob, cancelCsvUploadJob, UploadCancelledByUserError } from '../utils/csvUploadPolling';
import { getCachedUsers } from '../cache/appDataCache';

interface UploadResult {
  uploadTask?: {
    fileName: string;
    totalRecords: number;
    validRecords: number;
    invalidRecords: number;
    duplicateRecords: number;
  };
  adminCount?: number;
  assignmentBreakdown?: Array<{
    taskId: string;
    assignedTo: string | null;
    assignedToName: string;
    assignedToEmail?: string | null;
    newDomainsAssigned: number;
  }>;
  summary: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
    uniqueRows: number;
    existingUpdates?: number;
    movedFromFinal?: number;
    movedFromRejected?: number;
    lmSkipped?: number;
    duplicateRows: number;
    duplicatesInCSV?: number;
    duplicatesInSystem?: number;
    duplicatesInMainProject?: number;
    duplicatesInCurrentSystem?: number;
    priceSkipped?: number;
    priceUpdates?: number;
    fieldUpdates?: number;
    rejectedSkipped?: number;
  };
  invalidRows?: Array<{
    row: number;
    data: any;
    errors: string[];
  }>;
  duplicateDomains?: string[];
  duplicateDetails?: Array<{
    domain: string;
    source: string;
  }>;
  csvDuplicates?: string[];
  newDomains?: string[];
  priceSkippedDomains?: Array<{
    domain: string;
    reason: string;
    currentPrice?: number;
    newPrice?: number;
  }>;
  priceUpdatedDomains?: Array<{
    domain: string;
    currentPrice: number;
    newPrice: number;
  }>;
  fieldUpdatedDomains?: Array<{
    domain: string;
    updatedFields: string[];
  }>;
  movedFromFinalDomains?: string[];
  movedFromRejectedDomains?: string[];
  lmSkippedDomains?: Array<{ domain: string; source: string }>;
  rejectedSkippedUrls?: string[];
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

const UploadCSV: React.FC = () => {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState('');
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const [uploadProcessed, setUploadProcessed] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  const cancelRequestedRef = useRef(false);
  const activeJobIdRef = useRef<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showDuplicatesDialog, setShowDuplicatesDialog] = useState(false);
  const [showNewDomainsDialog, setShowNewDomainsDialog] = useState(false);
  const [showInvalidDialog, setShowInvalidDialog] = useState(false);
  const [showPriceUpdatesDialog, setShowPriceUpdatesDialog] = useState(false);
  const [showFieldUpdatesDialog, setShowFieldUpdatesDialog] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Get current user role
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isContributor = currentUser.role === 'CONTRIBUTOR';

  // Fetch admin users on component mount (only for SUPER_ADMIN)
  React.useEffect(() => {
    // Only SUPER_ADMIN needs to fetch users for assignment
    if (isContributor) {
      return;
    }

    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const allUsers = await getCachedUsers();
        // Filter only Admin users (not Super Admin)
        const adminUsers = allUsers.filter((user: User) => user.role === 'ADMIN');
        setUsers(adminUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [isContributor]);

  const handleDownloadTemplate = async (templateType: 'basic' | 'price' | 'full' = 'basic') => {
    setDownloading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      let endpoint: string;
      let filename: string;
      
      switch (templateType) {
        case 'price':
          endpoint = `${API_BASE_URL}/upload/template-with-price`;
          filename = 'guest_blog_template_with_price.csv';
          break;
        case 'full':
          endpoint = `${API_BASE_URL}/upload/template-full`;
          filename = 'guest_blog_full_template.csv';
          break;
        default:
          endpoint = `${API_BASE_URL}/upload/template`;
          filename = 'guest_blog_template.csv';
      }
        
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading template:', err);
      setError('Failed to download template. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setError('');
      setSuccess('');
      setUploadResult(null);
    }
  };

  const resetFileInput = () => {
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const applyUploadResult = (data: any) => {
    if (data.success) {
      setSuccess(data.message || 'CSV file uploaded successfully!');
      setUploadResult(data.data);
      setSelectedFile(null);
      resetFileInput();
    } else if (!data.success && data.message?.includes('All domains are duplicates')) {
      setError(data.message);
      setUploadResult(data.data);
      setSelectedFile(null);
      resetFileInput();
    } else {
      const errorData = data.error;
      let errorMessage = data.message || 'Failed to upload file. Please try again.';
      if (errorData?.message) {
        errorMessage = errorData.message;
        if (errorData.details) {
          errorMessage += ` ${errorData.details}`;
        }
      }
      setError(errorMessage);
    }
  };

  const handleCancelUpload = async () => {
    if (!uploading || cancelling) return;
    cancelRequestedRef.current = true;
    setCancelling(true);
    setUploadPhase('Cancelling...');

    const jobId = activeJobIdRef.current;
    const token = localStorage.getItem('token');
    if (jobId && token) {
      try {
        await cancelCsvUploadJob(jobId, token);
      } catch (err) {
        console.error('Failed to cancel upload job:', err);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    // Assignment is optional for update-only reuploads (all domains already in tool).
    // Backend still requires assignment when there are new domains.

    cancelRequestedRef.current = false;
    activeJobIdRef.current = null;
    setUploading(true);
    setCancelling(false);
    setUploadPhase('Uploading file...');
    setUploadPercent(1);
    setUploadProcessed(0);
    setUploadTotal(0);
    setError('');
    setSuccess('');
    setUploadResult(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      // Only add assignedTo for non-CONTRIBUTOR users
      if (!isContributor && assignedTo.length > 0) {
        formData.append('assignedTo', JSON.stringify(assignedTo));
      }

      const response = await axios.post(`${API_BASE_URL}/upload/csv`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000 // only waiting for file upload + 202 response
      });

      // Background job: poll until processing finishes (avoids nginx 504 on large CSVs)
      if (response.status === 202 && response.data.processing && response.data.data?.jobId) {
        const jobId = response.data.data.jobId as string;
        activeJobIdRef.current = jobId;

        if (cancelRequestedRef.current) {
          await cancelCsvUploadJob(jobId, token!);
          throw new UploadCancelledByUserError();
        }

        setUploadPhase('File received. Starting checks...');
        setUploadPercent(5);
        const jobResult = await pollCsvUploadJob(
          jobId,
          token!,
          (progress) => {
            if (progress.phase) setUploadPhase(progress.phase);
            if (typeof progress.percent === 'number') {
              setUploadPercent(Math.min(100, Math.max(0, progress.percent)));
            }
            if (typeof progress.processedUrls === 'number') {
              setUploadProcessed(progress.processedUrls);
            }
            if (typeof progress.totalUrls === 'number') {
              setUploadTotal(progress.totalUrls);
            }
          },
          () => cancelRequestedRef.current
        );
        setUploadPercent(100);
        applyUploadResult(jobResult.body);
        return;
      }

      setUploadPercent(100);
      applyUploadResult(response.data);
    } catch (err: any) {
      if (err instanceof UploadCancelledByUserError || err?.name === 'UploadCancelledByUserError') {
        setError('');
        setSuccess('Upload cancelled. No further rows will be processed.');
        return;
      }
      console.error('Error uploading file:', err);
      const errorData = err.response?.data?.error;
      let errorMessage = err.message || 'Failed to upload file. Please try again.';
      
      if (errorData) {
        errorMessage = errorData.message;
        if (errorData.details) {
          errorMessage += ` ${errorData.details}`;
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
    } finally {
      setUploading(false);
      setCancelling(false);
      setUploadPhase('');
      setUploadPercent(null);
      setUploadProcessed(0);
      setUploadTotal(0);
      activeJobIdRef.current = null;
      cancelRequestedRef.current = false;
    }
  };

  const uploadRemaining = Math.max(0, uploadTotal - uploadProcessed);

  const uploadStages = useMemo(() => {
    const phase = (uploadPhase || '').toLowerCase();
    const stages = [
      { key: 'parse', label: 'Parsing CSV', match: ['parsing'] },
      { key: 'local', label: 'Checking local database', match: ['local database'] },
      { key: 'guest', label: 'Checking main tool sites', match: ['main tool sites'] },
      { key: 'pending', label: 'Checking pending sites', match: ['checking pending sites'] },
      { key: 'prices', label: 'Checking prices', match: ['checking prices'] },
      {
        key: 'pending_prices',
        label: 'Checking pending prices',
        match: ['pending prices']
      },
      { key: 'publishers', label: 'Matching publishers', match: ['matching publishers'] },
      { key: 'saving', label: 'Saving records', match: ['saving records'] }
    ];

    let currentIdx = -1;
    // Prefer more specific phases first (pending prices before prices)
    if (phase.includes('pending prices')) {
      currentIdx = stages.findIndex((s) => s.key === 'pending_prices');
    } else {
      for (let i = 0; i < stages.length; i++) {
        if (stages[i].match.some((m) => phase.includes(m))) {
          currentIdx = i;
          break;
        }
      }
    }
    // Fallback: after file received / starting
    if (currentIdx < 0 && (phase.includes('starting') || phase.includes('file received') || phase.includes('uploading'))) {
      currentIdx = 0;
    }

    return stages.map((s, idx) => ({
      ...s,
      status:
        currentIdx < 0
          ? ('pending' as const)
          : idx < currentIdx
            ? ('done' as const)
            : idx === currentIdx
              ? ('current' as const)
              : ('pending' as const)
    }));
  }, [uploadPhase]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Upload CSV File
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Upload a CSV file containing guest blog website data for validation
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          <Box>
            <Typography variant="body2">{error}</Typography>
            {/* Show duplicate domain names when all domains are duplicates */}
            {error.includes('All domains are duplicates') && uploadResult?.duplicateDomains && uploadResult.duplicateDomains.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  Duplicate sites:
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                  {uploadResult.duplicateDomains.slice(0, 3).join(', ')}
                  {uploadResult.duplicateDomains.length > 3 && (
                    <Chip
                      label={`+${uploadResult.duplicateDomains.length - 3} more`}
                      size="small"
                      color="error"
                      onClick={() => setShowDuplicatesDialog(true)}
                      sx={{ cursor: 'pointer', fontSize: '0.7rem', ml: 1, height: '20px' }}
                    />
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {uploadResult && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CheckIcon sx={{ color: 'success.main', mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Upload Complete!
            </Typography>
          </Box>

          {uploadResult.assignmentBreakdown && uploadResult.assignmentBreakdown.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Assignment
              </Typography>
              {uploadResult.assignmentBreakdown.map((a) => (
                <Typography key={a.taskId} variant="body2">
                  <strong>{a.assignedToName}</strong>
                  {a.assignedToEmail ? ` (${a.assignedToEmail})` : ''}:{' '}
                  {a.newDomainsAssigned.toLocaleString()} new domain(s) added to their Data Management queue
                </Typography>
              ))}
              {(uploadResult.summary.existingUpdates || 0) > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {(uploadResult.summary.existingUpdates || 0).toLocaleString()} existing Data Management site(s)
                  were updated in place (original assignee unchanged).
                </Typography>
              )}
              {(uploadResult.summary.movedFromFinal || 0) > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {(uploadResult.summary.movedFromFinal || 0).toLocaleString()} moved from Data Final → Data Management.
                </Typography>
              )}
              {(uploadResult.summary.movedFromRejected || 0) > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {(uploadResult.summary.movedFromRejected || 0).toLocaleString()} moved from Rejected → Data Management.
                </Typography>
              )}
              {(uploadResult.summary.lmSkipped || 0) > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {(uploadResult.summary.lmSkipped || 0).toLocaleString()} skipped (already in Link Management / Pushed Data).
                </Typography>
              )}
            </Alert>
          )}

          {!uploadResult.assignmentBreakdown?.length && (uploadResult.summary.existingUpdates || 0) > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No new domains were assigned. {(uploadResult.summary.existingUpdates || 0).toLocaleString()} existing
              Data Management site(s) were updated in place
              {(uploadResult.summary.lmSkipped || 0) > 0
                ? `; ${(uploadResult.summary.lmSkipped || 0).toLocaleString()} skipped (Link Management / Pushed Data)`
                : ''}
              .
            </Alert>
          )}

          <Box sx={{ borderTop: '2px solid #ddd', borderBottom: '2px solid #ddd', py: 2, my: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              📊 <strong>Total Domains in file:</strong> {uploadResult.summary.totalRows.toLocaleString()}
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ color: 'success.main' }}>
                  ✅ <strong>Added to Data Management:</strong> {uploadResult.summary.uniqueRows.toLocaleString()}
                </Typography>
                {uploadResult.newDomains && uploadResult.newDomains.length > 5 && (
                  <Chip 
                    label={`View all ${uploadResult.newDomains.length}`}
                    size="small"
                    color="success"
                    onClick={() => setShowNewDomainsDialog(true)}
                    sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                  />
                )}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 3, mt: 0.5 }}>
                Includes brand-new sites plus moves from Data Final / Rejected (assigned to selected admin).
              </Typography>
              {uploadResult.newDomains && uploadResult.newDomains.length > 0 && uploadResult.newDomains.length <= 5 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 3, fontStyle: 'italic' }}>
                  {uploadResult.newDomains.join(', ')}
                </Typography>
              )}
              {uploadResult.newDomains && uploadResult.newDomains.length > 5 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 3, fontStyle: 'italic' }}>
                  {uploadResult.newDomains.slice(0, 5).join(', ')}... (click to view all)
                </Typography>
              )}
            </Box>
            
            {/* Moved from Final / Rejected */}
            {((uploadResult.summary.movedFromFinal || 0) > 0 || (uploadResult.summary.movedFromRejected || 0) > 0) && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body1" sx={{ color: 'secondary.main' }}>
                  📦 <strong>Moved into Data Management:</strong>
                </Typography>
                {(uploadResult.summary.movedFromFinal || 0) > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 3, mt: 0.5 }}>
                    • {(uploadResult.summary.movedFromFinal || 0).toLocaleString()} from Data Final (removed from Final)
                  </Typography>
                )}
                {(uploadResult.summary.movedFromRejected || 0) > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 3, mt: 0.5 }}>
                    • {(uploadResult.summary.movedFromRejected || 0).toLocaleString()} from Rejected (removed from Rejected)
                  </Typography>
                )}
              </Box>
            )}

            {/* Price Updates Section - Show when sites will be updated with lower prices */}
            {uploadResult.summary.priceUpdates && uploadResult.summary.priceUpdates > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ color: 'info.main' }}>
                    💰 <strong>Price Updates (existing sites):</strong> {uploadResult.summary.priceUpdates}
                  </Typography>
                  {uploadResult.priceUpdatedDomains && uploadResult.priceUpdatedDomains.length > 5 && (
                    <Chip 
                      label={`View all ${uploadResult.priceUpdatedDomains.length}`}
                      size="small"
                      color="info"
                      onClick={() => setShowPriceUpdatesDialog(true)}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 3, mt: 0.5 }}>
                  Lower price applied on existing records — original assignee unchanged
                </Typography>
                {uploadResult.priceUpdatedDomains && uploadResult.priceUpdatedDomains.length > 0 && (
                  <Box sx={{ ml: 3, mt: 0.5 }}>
                    {uploadResult.priceUpdatedDomains.slice(0, 5).map((d, idx) => (
                      <Typography key={idx} variant="caption" sx={{ color: 'info.main', display: 'block', fontStyle: 'italic' }}>
                        • {d.domain} (${d.currentPrice} → ${d.newPrice})
                      </Typography>
                    ))}
                    {uploadResult.priceUpdatedDomains.length > 5 && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontStyle: 'italic', cursor: 'pointer' }} onClick={() => setShowPriceUpdatesDialog(true)}>
                        ... and {uploadResult.priceUpdatedDomains.length - 5} more (click to view all)
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Field Updates Section - Show when existing domains get new/changed non-price fields */}
            {uploadResult.summary.fieldUpdates && uploadResult.summary.fieldUpdates > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ color: 'primary.main' }}>
                    🔄 <strong>Data Updates (existing sites):</strong> {uploadResult.summary.fieldUpdates}
                  </Typography>
                  {uploadResult.fieldUpdatedDomains && uploadResult.fieldUpdatedDomains.length > 5 && (
                    <Chip
                      label={`View all ${uploadResult.fieldUpdatedDomains.length}`}
                      size="small"
                      color="primary"
                      onClick={() => setShowFieldUpdatesDialog(true)}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 3, mt: 0.5 }}>
                  Fields updated on existing domains (DA, DR, traffic, publisher, etc.) — original assignee unchanged
                </Typography>
                {uploadResult.fieldUpdatedDomains && uploadResult.fieldUpdatedDomains.length > 0 && (
                  <Box sx={{ ml: 3, mt: 0.5 }}>
                    {uploadResult.fieldUpdatedDomains.slice(0, 5).map((d, idx) => (
                      <Typography key={idx} variant="caption" sx={{ color: 'primary.main', display: 'block', fontStyle: 'italic' }}>
                        • {d.domain} ({d.updatedFields.join(', ')})
                      </Typography>
                    ))}
                    {uploadResult.fieldUpdatedDomains.length > 5 && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block', fontStyle: 'italic', cursor: 'pointer' }}
                        onClick={() => setShowFieldUpdatesDialog(true)}
                      >
                        ... and {uploadResult.fieldUpdatedDomains.length - 5} more (click to view all)
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Skipped Section */}
            {(() => {
              const csvDupCount = uploadResult.summary.duplicatesInCSV || 0;
              const lmSkippedCount = uploadResult.summary.lmSkipped || uploadResult.summary.duplicatesInMainProject || 0;
              const priceSkippedCount = uploadResult.summary.priceSkipped || 0;
              
              // Avoid double-counting: lmSkipped is already reflected in priceSkipped for LM rows
              const finalSkipped = csvDupCount + Math.max(lmSkippedCount, 0);
              
              if (finalSkipped <= 0 && priceSkippedCount <= 0) return null;
              
              return (
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ color: 'warning.main' }}>
                      ⏭️ <strong>Skipped:</strong> {Math.max(finalSkipped, priceSkippedCount)}
                    </Typography>
                    <Chip 
                      label="View details"
                      size="small"
                      color="warning"
                      onClick={() => setShowDuplicatesDialog(true)}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                  </Box>
                  
                  {csvDupCount > 0 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 3, mt: 0.5 }}>
                      • {csvDupCount} duplicate(s) within CSV file
                    </Typography>
                  )}
                  {lmSkippedCount > 0 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 3, mt: 0.5 }}>
                      • {lmSkippedCount} already in Link Management / Pushed Data
                    </Typography>
                  )}
                </Box>
              );
            })()}
            
            {uploadResult.summary.invalidRows > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ color: 'error.main' }}>
                    ❌ <strong>Invalid Records:</strong> {uploadResult.summary.invalidRows}
                  </Typography>
                  {uploadResult.invalidRows && uploadResult.invalidRows.length > 5 && (
                    <Chip 
                      label={`+${uploadResult.invalidRows.length - 5} more`}
                      size="small"
                      color="error"
                      onClick={() => setShowInvalidDialog(true)}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
                {uploadResult.invalidRows && uploadResult.invalidRows.length > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 3, fontStyle: 'italic' }}>
                    {uploadResult.invalidRows.slice(0, 5).map(inv => {
                      const domain = inv.data.site || inv.data.domain || 'Unknown';
                      return `${domain} (${inv.errors.join(', ')})`;
                    }).join('; ')}
                    {uploadResult.invalidRows.length > 5 && '...'}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          {uploadResult.uploadTask && (
            <Typography variant="body2" color="text.secondary">
              File: {uploadResult.uploadTask.fileName}
            </Typography>
          )}
        </Paper>
      )}

      <Card>
        <CardContent>
          {/* Assign To Dropdown - Hidden for CONTRIBUTOR users */}
          {!isContributor && (
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="assign-to-label">Assign To Admin Users</InputLabel>
                <Select
                  labelId="assign-to-label"
                  id="assign-to"
                  multiple
                  value={assignedTo}
                  label="Assign To Admin Users"
                  onChange={(e) => setAssignedTo(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  disabled={loadingUsers}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((id) => {
                        const user = users.find(u => u.id === id);
                        return (
                          <Chip 
                            key={id} 
                            label={user ? `${user.firstName} ${user.lastName}` : id}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      <Checkbox checked={assignedTo.indexOf(user.id) > -1} />
                      <ListItemText primary={`${user.firstName} ${user.lastName} (${user.email})`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Required when adding sites to Data Management (new or moved from Final/Rejected). Sites already in Link Management / Pushed Data are skipped. Existing Data Management rows are updated in place when metrics change.
              </Typography>
            </Box>
          )}
          
          {/* Info message for CONTRIBUTOR users */}
          {isContributor && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="info">
                Your uploads will be automatically assigned to your designated admin for review.
              </Alert>
            </Box>
          )}

          <Box
            sx={{
              border: selectedFile ? '2px solid #1976d2' : '2px dashed #ccc',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: selectedFile ? '#e3f2fd' : '#fafafa',
            }}
          >
            <UploadIcon sx={{ fontSize: 64, color: selectedFile ? '#1976d2' : '#999', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {selectedFile ? selectedFile.name : 'Drag and drop your CSV file here'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedFile ? `Size: ${(selectedFile.size / 1024).toFixed(2)} KB` : 'or click to browse'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="contained" component="label">
                {selectedFile ? 'Change File' : 'Select File'}
                <input type="file" hidden accept=".csv" onChange={handleFileSelect} />
              </Button>
              {selectedFile && (
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={handleUpload}
                  disabled={uploading}
                  startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                >
                  {uploading
                    ? (uploadPercent != null
                        ? `${uploadPercent}% — ${uploadPhase || 'Processing...'}`
                        : (uploadPhase || 'Processing...'))
                    : 'Upload'}
                </Button>
              )}
            </Box>
          </Box>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  {uploadPhase || 'Processing CSV file...'}
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                  {uploadPercent != null ? `${uploadPercent}%` : ''}
                </Typography>
              </Box>
              <LinearProgress
                variant={uploadPercent != null ? 'determinate' : 'indeterminate'}
                value={uploadPercent ?? 0}
                color={cancelling ? 'warning' : 'primary'}
                sx={{ height: 8, borderRadius: 999 }}
              />

              {uploadTotal > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}
                >
                  <Chip
                    size="small"
                    color="success"
                    icon={<CheckIcon sx={{ color: '#fff !important' }} />}
                    label={`Checked successfully: ${uploadProcessed.toLocaleString()}`}
                    sx={{
                      color: '#fff',
                      '& .MuiChip-icon': { color: '#fff !important' },
                      '& .MuiChip-label': { color: '#fff' }
                    }}
                  />
                  <Chip
                    size="small"
                    color={uploadRemaining > 0 ? 'warning' : 'success'}
                    icon={
                      uploadRemaining > 0 ? (
                        <BlankCircleIcon />
                      ) : (
                        <CheckIcon sx={{ color: '#fff !important' }} />
                      )
                    }
                    label={`Remaining: ${uploadRemaining.toLocaleString()}`}
                    sx={
                      uploadRemaining > 0
                        ? undefined
                        : {
                            color: '#fff',
                            '& .MuiChip-icon': { color: '#fff !important' },
                            '& .MuiChip-label': { color: '#fff' }
                          }
                    }
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Total: ${uploadTotal.toLocaleString()}`}
                  />
                </Box>
              )}

              <Paper variant="outlined" sx={{ mt: 2, p: 1.5, textAlign: 'left' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Check progress
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.75,
                    rowGap: 1
                  }}
                >
                  {uploadStages.map((stage, idx) => {
                    const isDone = stage.status === 'done';
                    const isCurrent = stage.status === 'current';
                    const stagePct =
                      isCurrent && uploadTotal > 0
                        ? Math.min(100, Math.round((uploadProcessed / uploadTotal) * 100))
                        : isDone
                          ? 100
                          : 0;

                    return (
                      <React.Fragment key={stage.key}>
                        {idx > 0 && (
                          <Typography variant="body2" color="text.disabled" sx={{ px: 0.25 }}>
                            →
                          </Typography>
                        )}
                        <Chip
                          size="small"
                          icon={
                            isDone ? (
                              <CheckIcon sx={{ color: '#fff !important' }} />
                            ) : isCurrent ? (
                              <CircularProgress size={14} thickness={5} sx={{ color: '#fff !important' }} />
                            ) : (
                              <BlankCircleIcon />
                            )
                          }
                          label={
                            isCurrent && uploadTotal > 0
                              ? `${stage.label} · ${stagePct}%`
                              : stage.label
                          }
                          color={isDone ? 'success' : isCurrent ? 'primary' : 'default'}
                          variant={isDone || isCurrent ? 'filled' : 'outlined'}
                          sx={{
                            fontWeight: isCurrent ? 600 : 400,
                            opacity: isDone || isCurrent ? 1 : 0.65,
                            ...(isDone || isCurrent
                              ? {
                                  color: '#fff',
                                  '& .MuiChip-icon': { color: '#fff !important', ml: 0.5 },
                                  '& .MuiChip-label': { color: '#fff' }
                                }
                              : {
                                  '& .MuiChip-icon': { color: 'text.disabled', ml: 0.5 }
                                })
                          }}
                        />
                      </React.Fragment>
                    );
                  })}
                </Box>
                {uploadTotal > 0 && uploadStages.some((s) => s.status === 'current') && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Current step: {uploadProcessed.toLocaleString()} complete ·{' '}
                    {uploadRemaining.toLocaleString()} remaining
                  </Typography>
                )}
              </Paper>

              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Large files may take several minutes. Please keep this page open.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleCancelUpload}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel upload'}
                </Button>
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
              size="medium"
              onClick={() => handleDownloadTemplate('full')}
              disabled={downloading}
            >
              {downloading ? 'Downloading...' : 'Data CSV Template'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Supported Fields Information */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            📋 Supported CSV Fields
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your CSV file can include any of these fields. Only fields with data will be imported (empty fields are skipped):
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main', mb: 1 }}>
                🔹 Required Fields
              </Typography>
              <Box sx={{ ml: 2 }}>
                <Typography variant="body2">• <strong>Site/Domain/URL</strong> - Website URL (required)</Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'info.main', mb: 1 }}>
                🔹 Optional Fields
              </Typography>
              <Box sx={{ ml: 2 }}>
                <Typography variant="body2">• <strong>Publisher</strong> - Publisher name or email</Typography>
                <Typography variant="body2">• <strong>GB Base Price/Price</strong> - Guest blog price</Typography>
                <Typography variant="body2">• <strong>LI Base Price</strong> - Link insertion price</Typography>
                <Typography variant="body2">• <strong>DA/Domain Authority</strong> - Domain Authority (0-100)</Typography>
                <Typography variant="body2">• <strong>DR/Domain Rating</strong> - Domain Rating (0-100)</Typography>
                <Typography variant="body2">• <strong>Traffic</strong> - Monthly traffic</Typography>
                <Typography variant="body2">• <strong>SS/Spam Score</strong> - Spam Score (0-100)</Typography>
                <Typography variant="body2">• <strong>Keywords</strong> - Number of keywords</Typography>
                <Typography variant="body2">• <strong>Domain Age / domain_age</strong> - Domain age in years (number only, e.g. 6 — shown as "6 years" in tables)</Typography>
                <Typography variant="body2">• <strong>Category</strong> - Website category</Typography>
                <Typography variant="body2">• <strong>Country</strong> - Website country</Typography>
                <Typography variant="body2">• <strong>Language</strong> - Website language</Typography>
                <Typography variant="body2">• <strong>TAT/Turnaround Time</strong> - Delivery time</Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Duplicates Dialog */}
      <Dialog 
        open={showDuplicatesDialog} 
        onClose={() => setShowDuplicatesDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          ⏭️ Skipped Domains (Already Exist)
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {/* CSV Duplicates */}
          {uploadResult?.csvDuplicates && uploadResult.csvDuplicates.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 0.5 }}>
                📄 Duplicates within CSV file ({uploadResult.csvDuplicates.length})
              </Typography>
              {uploadResult.csvDuplicates.map((domain, i) => (
                <Typography key={i} variant="body2" sx={{ ml: 2, fontFamily: 'monospace', py: 0.25 }}>{domain}</Typography>
              ))}
            </Box>
          )}

          {/* LM / Pushed skips */}
          {uploadResult?.lmSkippedDomains && uploadResult.lmSkippedDomains.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 0.5 }}>
                💾 Already in Link Management / Pushed Data ({uploadResult.lmSkippedDomains.length})
              </Typography>
              {uploadResult.lmSkippedDomains.map((item, i) => (
                <Typography key={i} variant="body2" sx={{ ml: 2, fontFamily: 'monospace', py: 0.25 }}>
                  {item.domain}
                </Typography>
              ))}
            </Box>
          )}

          {/* System Duplicates - grouped by source (exclude price-skipped/updated and LM skips shown above) */}
          {uploadResult?.duplicateDetails && uploadResult.duplicateDetails.length > 0 && (() => {
            const priceRelated = new Set([
              ...(uploadResult.priceSkippedDomains || []).map(d => d.domain.toLowerCase()),
              ...(uploadResult.priceUpdatedDomains || []).map(d => d.domain.toLowerCase()),
              ...(uploadResult.lmSkippedDomains || []).map(d => d.domain.toLowerCase())
            ]);
            const filtered = uploadResult.duplicateDetails.filter(
              (item) =>
                !priceRelated.has(item.domain.toLowerCase()) &&
                !item.source.includes('Links Management') &&
                item.source !== 'Pushed Data' &&
                item.source !== 'Rejected Data' &&
                !item.source.includes('(Final)')
            );
            if (filtered.length === 0) return null;
            const grouped: Record<string, string[]> = {};
            filtered.forEach(item => {
              if (!grouped[item.source]) grouped[item.source] = [];
              grouped[item.source].push(item.domain);
            });
            return Object.entries(grouped).map(([source, domains]) => (
              <Box key={source} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 0.5 }}>
                  💾 {source} ({domains.length})
                </Typography>
                {domains.map((domain, i) => (
                  <Typography key={i} variant="body2" sx={{ ml: 2, fontFamily: 'monospace', py: 0.25 }}>{domain}</Typography>
                ))}
              </Box>
            ));
          })()}

          {/* Fallback: show CSV duplicate count if no detailed list */}
          {(!uploadResult?.csvDuplicates || uploadResult.csvDuplicates.length === 0) && 
           uploadResult?.summary?.duplicatesInCSV && uploadResult.summary.duplicatesInCSV > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 0.5 }}>
                📄 Duplicates within CSV file ({uploadResult.summary.duplicatesInCSV})
              </Typography>
              <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                {uploadResult.summary.duplicatesInCSV} duplicate row(s) found in the uploaded CSV
              </Typography>
            </Box>
          )}

          {/* Fallback when detailed LM list is missing */}
          {(!uploadResult?.lmSkippedDomains || uploadResult.lmSkippedDomains.length === 0) &&
            (uploadResult?.summary?.lmSkipped || uploadResult?.summary?.duplicatesInMainProject || 0) > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 0.5 }}>
                💾 Already in Link Management / Pushed Data (
                {(uploadResult?.summary?.lmSkipped || uploadResult?.summary?.duplicatesInMainProject || 0)}
                )
              </Typography>
              <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                Site(s) already exist in Link Management or Pushed Data and were skipped
              </Typography>
            </Box>
          )}

          {/* Previously Rejected — legacy field; empty under move-to-DM rules */}
          {uploadResult?.rejectedSkippedUrls && uploadResult.rejectedSkippedUrls.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.main', mb: 0.5 }}>
                🚫 Previously Rejected ({uploadResult.rejectedSkippedUrls.length})
              </Typography>
              {uploadResult.rejectedSkippedUrls.map((url, i) => (
                <Typography key={i} variant="body2" sx={{ ml: 2, fontFamily: 'monospace', py: 0.25 }}>{url}</Typography>
              ))}
            </Box>
          )}

          {/* Price Skipped */}
          {uploadResult?.priceSkippedDomains && uploadResult.priceSkippedDomains.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'info.main', mb: 0.5 }}>
                💰 Skipped — Same/Higher Price or No Price ({uploadResult.priceSkippedDomains.length})
              </Typography>
              {uploadResult.priceSkippedDomains.map((item, i) => (
                <Box key={i} sx={{ ml: 2, py: 0.25, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{item.domain}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>— {item.reason}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDuplicatesDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Domains Dialog */}
      <Dialog 
        open={showNewDomainsDialog} 
        onClose={() => setShowNewDomainsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
          ✅ New Domains Added
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These new domains were successfully added to the system:
          </Typography>
          <List dense>
            {uploadResult?.newDomains?.map((domain, index) => (
              <ListItem key={index} sx={{ py: 0.5, pl: 2 }}>
                <ListItemText 
                  primary={`${index + 1}. ${domain}`}
                  primaryTypographyProps={{ 
                    variant: 'body2',
                    sx: { fontFamily: 'monospace' }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewDomainsDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invalid Records Dialog */}
      <Dialog 
        open={showInvalidDialog} 
        onClose={() => setShowInvalidDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
          ❌ Invalid Records
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These records have validation errors and were not processed:
          </Typography>
          <List dense>
            {uploadResult?.invalidRows?.map((inv, index) => {
              const domain = inv.data.site || inv.data.domain || 'Unknown';
              return (
                <ListItem key={index} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                  <ListItemText 
                    primary={domain}
                    secondary={`Error: ${inv.errors.join(', ')}`}
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      sx: { fontFamily: 'monospace', fontWeight: 'bold' }
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'error'
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInvalidDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Price Updates Dialog */}
      <Dialog 
        open={showPriceUpdatesDialog} 
        onClose={() => setShowPriceUpdatesDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
          💰 Price Updates - Lower Prices Found
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These sites already exist in the system but were found with lower prices. They will be processed for price update:
          </Typography>
          <List dense>
            {uploadResult?.priceUpdatedDomains?.map((item, index) => (
              <ListItem key={index} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                <ListItemText 
                  primary={item.domain}
                  secondary={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                        Current: <strong>${item.currentPrice}</strong>
                      </Typography>
                      <Typography component="span" variant="caption">→</Typography>
                      <Typography component="span" variant="caption" sx={{ color: 'success.main' }}>
                        New: <strong>${item.newPrice}</strong>
                      </Typography>
                      <Typography component="span" variant="caption" sx={{ color: 'success.main', ml: 1 }}>
                        (Save ${item.currentPrice - item.newPrice})
                      </Typography>
                    </Box>
                  }
                  primaryTypographyProps={{ 
                    variant: 'body2',
                    sx: { fontFamily: 'monospace', fontWeight: 'bold' }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPriceUpdatesDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Field Updates Dialog */}
      <Dialog
        open={showFieldUpdatesDialog}
        onClose={() => setShowFieldUpdatesDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          🔄 Data Updates - Existing Domains Updated
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These domains already exist in the tool and were updated with new or changed field values from your CSV:
          </Typography>
          <List dense>
            {uploadResult?.fieldUpdatedDomains?.map((item, index) => (
              <ListItem key={index} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                <ListItemText
                  primary={item.domain}
                  secondary={`Updated fields: ${item.updatedFields.join(', ')}`}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { fontFamily: 'monospace', fontWeight: 'bold' }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFieldUpdatesDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadCSV;
